import { useEffect } from 'react'
import { cn } from '@/utils/cn'
import { signalSlowNavigation, signalNavigationComplete } from '@/components/layout/PageLoadingBar'

interface TutaraSkeletonProps {
  className?: string
}

/**
 * Base skeleton block with pulse animation.
 * Compose with className to create any shape.
 */
export function TutaraSkeleton({ className }: TutaraSkeletonProps) {
  return (
    <div
      className={cn(
        'bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse',
        className
      )}
    />
  )
}

/**
 * Skeleton for stat/overview cards (e.g. ProgressOverview, QuickStatCard).
 */
export function SkeletonStatsGrid({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <TutaraSkeleton key={i} className="h-[140px]" />
      ))}
    </div>
  )
}

/**
 * Skeleton for a card with a header title and content blocks.
 */
export function SkeletonCard({
  headerHeight = 'h-5',
  lines = 3,
  lineHeight = 'h-4',
  className,
}: {
  headerHeight?: string
  lines?: number
  lineHeight?: string
  className?: string
}) {
  return (
    <div className={cn('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4', className)}>
      <TutaraSkeleton className={cn('w-1/3', headerHeight)} />
      <div className="space-y-3">
        {Array.from({ length: lines }).map((_, i) => (
          <TutaraSkeleton
            key={i}
            className={cn(lineHeight, i === lines - 1 ? 'w-2/3' : 'w-full')}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Skeleton for a course/learning card in a grid.
 */
export function SkeletonCourseCard() {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4 animate-pulse">
      <div className="flex items-center gap-3">
        <TutaraSkeleton className="w-10 h-10 rounded-lg" />
        <div className="flex-1 space-y-2">
          <TutaraSkeleton className="h-4 w-3/4" />
          <TutaraSkeleton className="h-3 w-1/2" />
        </div>
      </div>
      <TutaraSkeleton className="h-3 w-full" />
      <div className="flex justify-between">
        <TutaraSkeleton className="h-3 w-1/4" />
        <TutaraSkeleton className="h-3 w-1/4" />
      </div>
    </div>
  )
}

/**
 * Skeleton for a list of items inside a card (e.g. CourseProgressList, SkillsProgress).
 */
export function SkeletonListItems({ count = 3, itemHeight = 'h-24' }: { count?: number; itemHeight?: string }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <TutaraSkeleton key={i} className={itemHeight} />
      ))}
    </div>
  )
}

/**
 * Skeleton for a chart area.
 */
export function SkeletonChart({ height = 'h-64' }: { height?: string }) {
  return <TutaraSkeleton className={height} />
}

/**
 * Full-page loading skeleton that replaces the bare spinner.
 * Shows a header + card grid to maintain layout structure.
 */
export function PageSkeleton() {
  useEffect(() => {
    // Tell PageLoadingBar a lazy chunk is loading — cancel the fast-complete timeout
    signalSlowNavigation()
    // When this fallback unmounts (chunk loaded), complete the bar
    return () => { signalNavigationComplete() }
  }, [])

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-pulse">
      {/* Header skeleton */}
      <div className="space-y-2">
        <TutaraSkeleton className="h-8 w-1/3" />
        <TutaraSkeleton className="h-4 w-1/2" />
      </div>
      {/* Content skeleton */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonCourseCard key={i} />
        ))}
      </div>
    </div>
  )
}
